import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { APP_STORE_URL, PLAY_STORE_URL } from "@/lib/webAccess";
import { planDisplay } from "@/lib/plan";
import {
  Smartphone, CreditCard, Users, Settings, LifeBuoy, ChevronRight, Apple, Play,
} from "lucide-react";

const QUICK_ACTIONS = [
  { to: "/dashboard/billing", icon: CreditCard, label: "Subscription & Billing", desc: "Manage your plan, seats and payment.", roles: ["owner", "manager"], testid: "portal-billing" },
];

function StoreButton({ href, Icon, top, bottom, testid }) {
  const disabled = !href;
  const inner = (
    <span
      className={`flex items-center gap-3 px-5 py-3 rounded-md border transition-colors ${
        disabled
          ? "border-[#27272A] bg-[#0A0A0A] text-[#71717A] cursor-default"
          : "border-[#27272A] bg-[#121212] text-white hover:border-[#178AE5]"
      }`}
    >
      <Icon className="w-6 h-6 shrink-0" />
      <span className="text-left leading-tight">
        <span className="block text-[10px] font-label tracking-widest text-[#A1A1AA]">{top}</span>
        <span className="block font-display text-base">{bottom}</span>
      </span>
    </span>
  );
  if (disabled) return <div data-testid={`${testid}-soon`} title="Coming soon — currently in review">{inner}</div>;
  return <a href={href} target="_blank" rel="noreferrer" data-testid={testid}>{inner}</a>;
}

export default function PortalHome() {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);

  useEffect(() => {
    // Reconcile with Stripe first so the renew/cancel state is fresh, then load.
    api.get("/billing/sync").catch(() => {}).finally(() => {
      api.get("/companies/me").then((r) => setCompany(r.data)).catch(() => {});
    });
  }, []);

  const actions = QUICK_ACTIONS.filter((a) => a.roles.includes(user?.role));
  const storesLive = APP_STORE_URL || PLAY_STORE_URL;

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;
  const subStatus = company?.subscription_status;
  const showRenewal = ["trial", "active", "past_due"].includes(subStatus);
  const willCancel = !!company?.cancel_at_period_end;
  const isTrialing = subStatus === "trial";
  const renewalNote = !showRenewal
    ? null
    : willCancel
      ? `Set to cancel${fmtDate(company?.cancel_at || company?.trial_ends_at) ? ` ${fmtDate(company?.cancel_at || company?.trial_ends_at)}` : ""} — won't renew`
      : isTrialing
        ? `Renews${fmtDate(company?.trial_ends_at) ? ` ${fmtDate(company?.trial_ends_at)}` : " after trial"}`
        : "Auto-renews";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="portal-home">
      {/* Welcome */}
      <div className="mb-8">
        <div className="font-label text-[10px] tracking-[0.4em] text-[#178AE5] mb-2">ACCOUNT PORTAL</div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight">
          Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
        </h1>
        <p className="text-[#A1A1AA] mt-2 max-w-2xl">
          The full SalesArena experience — leaderboards, the Arena, The Pit and everything your team competes in — now lives in the mobile app. Use this portal to manage your subscription, team and account.
        </p>
      </div>

      {/* Get the app */}
      <div
        className="rounded-lg border border-[#27272A] p-6 md:p-8 mb-8"
        style={{ background: "linear-gradient(135deg, rgba(23,138,229,0.12), transparent 60%)" }}
        data-testid="portal-app-card"
      >
        <div className="flex items-start gap-4">
          <div className="w-[106px] h-[106px] rounded-xl overflow-hidden shrink-0 border border-[#27272A]">
            <img src="/app-icon.png" alt="SalesArena HQ" className="w-full h-full object-cover" data-testid="portal-app-icon" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-2xl">Get the SalesArena app</h2>
            <p className="text-[#A1A1AA] mt-1 mb-4 text-sm">
              Reps and managers run the day-to-day on their phone — logging sales, battling in the Arena, and climbing the leaderboard.
            </p>
            <div className="flex flex-wrap gap-3">
              <StoreButton href={APP_STORE_URL} Icon={Apple} top="Download on the" bottom="App Store" testid="portal-appstore" />
              <StoreButton href={PLAY_STORE_URL} Icon={Play} top="Get it on" bottom="Google Play" testid="portal-playstore" />
            </div>
            {!storesLive && (
              <p className="text-[11px] text-[#71717A] mt-3" data-testid="portal-stores-review">
                Store links appear here once the app listings go live (currently in review).
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Account summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="portal-account-summary">
        <SummaryTile label="COMPANY" value={company?.name || "—"} />
        <SummaryTile label="PLAN" value={planDisplay(company)} note={renewalNote} noteCancel={willCancel} />
        <SummaryTile label="SEATS USED" value={`${company?.current_users ?? "—"} / ${company?.max_users ?? "∞"}`} />
      </div>

      {/* Quick actions */}
      {actions.length > 0 && (
        <>
          <div className="font-label text-[10px] tracking-[0.4em] text-[#A1A1AA] mb-3">MANAGE</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            {actions.map((a) => (
              <Link
                key={a.to}
                to={a.to}
                data-testid={a.testid}
                className="group flex items-center gap-4 rounded-md border border-[#27272A] bg-[#0A0A0A] hover:bg-[#121212] hover:border-[#178AE5] transition-colors p-4"
              >
                <div className="w-10 h-10 rounded-md bg-[#121212] border border-[#27272A] flex items-center justify-center shrink-0">
                  <a.icon className="w-5 h-5 text-[#178AE5]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base">{a.label}</div>
                  <div className="text-xs text-[#A1A1AA] truncate">{a.desc}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#52525B] group-hover:text-[#178AE5] shrink-0" />
              </Link>
            ))}
          </div>
        </>
      )}

      {/* Support */}
      <Link
        to="/dashboard/support"
        data-testid="portal-support"
        className="group flex items-center gap-4 rounded-md border border-[#27272A] bg-[#0A0A0A] hover:bg-[#121212] hover:border-[#FFC832] transition-colors p-4"
      >
        <div className="w-10 h-10 rounded-md bg-[#121212] border border-[#27272A] flex items-center justify-center shrink-0">
          <LifeBuoy className="w-5 h-5 text-[#FFC832]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base">Need help?</div>
          <div className="text-xs text-[#A1A1AA]">Premium support — pick a reason and we'll reply by email.</div>
        </div>
        <ChevronRight className="w-4 h-4 text-[#52525B] group-hover:text-[#FFC832] shrink-0" />
      </Link>
    </div>
  );
}

function SummaryTile({ label, value, note, noteCancel }) {
  return (
    <div className="rounded-md border border-[#27272A] bg-[#0A0A0A] p-4">
      <div className="font-label text-[10px] tracking-widest text-[#A1A1AA]">{label}</div>
      <div className="font-display text-xl mt-1 truncate">{value}</div>
      {note && (
        <div
          className={`text-xs mt-1 truncate ${noteCancel ? "text-[#E8921F]" : "text-[#34C759]"}`}
          data-testid="plan-renewal-note"
        >
          {note}
        </div>
      )}
    </div>
  );
}
